import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  useTheme,
  alpha,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  People as PeopleIcon,
  Inventory as InventoryIcon,
  AccountBalance as BankIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Error as ErrorIcon,
  AutoFixHigh as MagicIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import Papa from 'papaparse';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import indexedDBService from '../services/IndexedDBService';

type TemplateType = 'clients' | 'inventory' | 'bank';

interface TemplateDef {
  id: TemplateType;
  name: string;
  filename: string;
  headers: string[];
  requiredHeaders: string[];
  icon: JSX.Element;
  color: string;
}

const DataImport: React.FC = () => {
  const theme = useTheme();
  const { currentUser } = useAuth();
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  
  // Parsing and Mapping State
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateType | ''>('');
  const [parsedHeaders, setParsedHeaders] = useState<string[]>([]);
  const [parsedData, setParsedData] = useState<any[]>([]);
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});
  const [validationErrors, setValidationErrors] = useState<Record<number, string[]>>({});
  
  // Execution State
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Reconciliation State
  const [reconciliationMode, setReconciliationMode] = useState(false);
  const [suggestedMatches, setSuggestedMatches] = useState<Record<number, any>>({});
  const [approvedMatches, setApprovedMatches] = useState<Record<number, boolean>>({});

  const templates: TemplateDef[] = useMemo(() => [
    {
      id: 'clients',
      name: 'Clients & Vendors',
      filename: 'clients_template.csv',
      headers: ['Name', 'Email', 'Phone', 'GSTIN', 'Address'],
      requiredHeaders: ['Name'],
      icon: <PeopleIcon color="primary" />,
      color: theme.palette.primary.main
    },
    {
      id: 'inventory',
      name: 'Inventory Items',
      filename: 'inventory_template.csv',
      headers: ['Name', 'SKU', 'PurchasePrice', 'SellingPrice', 'Stock', 'HSN'],
      requiredHeaders: ['Name', 'SellingPrice'],
      icon: <InventoryIcon color="success" />,
      color: theme.palette.success.main
    },
    {
      id: 'bank',
      name: 'Bank Transactions',
      filename: 'bank_template.csv',
      headers: ['Date', 'Description', 'Amount', 'Type'],
      requiredHeaders: ['Date', 'Description', 'Amount', 'Type'],
      icon: <BankIcon color="info" />,
      color: theme.palette.info.main
    }
  ], [theme]);

  const activeTemplateDef = templates.find(t => t.id === selectedTemplate);

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault(); e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    if (!selectedTemplate) {
      toast.warning('Please select a Target Template Type first.');
      return;
    }
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFile(e.dataTransfer.files[0]);
    }
  }, [selectedTemplate]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedTemplate) {
      toast.warning('Please select a Target Template Type first.');
      return;
    }
    if (e.target.files && e.target.files.length > 0) {
      processFile(e.target.files[0]);
    }
  };

  const processFile = (file: File) => {
    if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
      setSelectedFile(file);
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          const headers = results.meta.fields || [];
          setParsedHeaders(headers);
          setParsedData(results.data);
          
          // Auto-mapping
          if (activeTemplateDef) {
            const initialMapping: Record<string, string> = {};
            activeTemplateDef.headers.forEach(reqHeader => {
              const match = headers.find(h => h.toLowerCase() === reqHeader.toLowerCase() || h.replace(/\s+/g, '').toLowerCase() === reqHeader.toLowerCase());
              if (match) {
                initialMapping[reqHeader] = match;
              } else {
                initialMapping[reqHeader] = '';
              }
            });
            setColumnMapping(initialMapping);
          }
        },
        error: (err) => {
          toast.error('Failed to parse CSV: ' + err.message);
        }
      });
    } else {
      toast.error('Please upload a valid CSV file.');
    }
  };

  useEffect(() => {
    if (parsedData.length > 0 && activeTemplateDef) {
      const errors: Record<number, string[]> = {};
      parsedData.forEach((row, index) => {
        const rowErrs: string[] = [];
        activeTemplateDef.requiredHeaders.forEach(reqHeader => {
          const mappedKey = columnMapping[reqHeader];
          if (!mappedKey || !row[mappedKey] || String(row[mappedKey]).trim() === '') {
            rowErrs.push(`Missing required field: ${reqHeader}`);
          }
        });
        
        if (activeTemplateDef.id === 'inventory') {
           const priceKey = columnMapping['SellingPrice'];
           if (priceKey && row[priceKey] && isNaN(parseFloat(row[priceKey]))) {
             rowErrs.push('SellingPrice must be a number');
           }
        } else if (activeTemplateDef.id === 'bank') {
           const amtKey = columnMapping['Amount'];
           if (amtKey && row[amtKey] && isNaN(parseFloat(row[amtKey]))) {
             rowErrs.push('Amount must be a number');
           }
        }

        if (rowErrs.length > 0) {
          errors[index] = rowErrs;
        }
      });
      setValidationErrors(errors);
    }
  }, [parsedData, columnMapping, activeTemplateDef]);

  const handleMappingChange = (reqHeader: string, uploadedHeader: string) => {
    setColumnMapping(prev => ({ ...prev, [reqHeader]: uploadedHeader }));
  };

  const generateAndDownloadCSV = (filename: string, headers: string[]) => {
    const csvContent = "data:text/csv;charset=utf-8," + headers.join(",");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const resetUpload = () => {
    setSelectedFile(null);
    setParsedData([]);
    setParsedHeaders([]);
    setColumnMapping({});
    setValidationErrors({});
    setReconciliationMode(false);
    setSuggestedMatches({});
    setApprovedMatches({});
    const input = document.getElementById('file-upload-input') as HTMLInputElement;
    if (input) input.value = '';
  };

  // ── Execution Logic ──

  const handleProcessData = async () => {
    if (!currentUser || !activeTemplateDef) return;
    setIsProcessing(true);

    try {
      // 1. Enter Reconciliation Mode for Bank imports
      if (activeTemplateDef.id === 'bank') {
        const invoices = await indexedDBService.getAllItems<any>('invoices');
        const unpaid = invoices.filter(i => i.userId === currentUser.uid && i.status !== 'paid');
        
        const matches: Record<number, any> = {};
        parsedData.forEach((row, idx) => {
            const amt = parseFloat(row[columnMapping['Amount']]);
            const typeRaw = row[columnMapping['Type']]?.toLowerCase() || '';
            const isCredit = ['inward', 'credit', 'deposit'].includes(typeRaw);
            
            if (isCredit && !isNaN(amt)) {
                // Exact balance match
                const exactMatch = unpaid.find(inv => {
                    const balance = parseFloat(inv.balanceDue || inv.totalAmount || 0);
                    return balance === amt;
                });
                if (exactMatch) matches[idx] = exactMatch;
            }
        });
        
        setSuggestedMatches(matches);
        setReconciliationMode(true);
        setIsProcessing(false);
        return;
      }

      // 2. Batch Insertion for Clients & Inventory
      const storeName = activeTemplateDef.id === 'clients' ? 'clients' : 'inventory';
      
      for (const row of parsedData) {
        const item: any = {
            id: uuidv4(),
            userId: currentUser.uid,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        };
        
        activeTemplateDef.headers.forEach(h => {
            const mappedKey = columnMapping[h];
            if (mappedKey) {
                if (activeTemplateDef.id === 'clients') {
                    if (h === 'Name') item.name = row[mappedKey];
                    if (h === 'Email') item.email = row[mappedKey];
                    if (h === 'Phone') item.phone = row[mappedKey];
                    if (h === 'GSTIN') item.gstin = row[mappedKey];
                    if (h === 'Address') item.address = row[mappedKey];
                }
                if (activeTemplateDef.id === 'inventory') {
                    if (h === 'Name') item.name = row[mappedKey];
                    if (h === 'SKU') item.sku = row[mappedKey];
                    if (h === 'PurchasePrice') item.purchasePrice = parseFloat(row[mappedKey]) || 0;
                    if (h === 'SellingPrice') item.sellingPrice = parseFloat(row[mappedKey]) || 0;
                    if (h === 'Stock') item.stockQuantity = parseFloat(row[mappedKey]) || 0;
                    if (h === 'HSN') item.hsn = row[mappedKey];
                }
            }
        });
        
        await indexedDBService.addItem(storeName, item);
      }

      toast.success(`Successfully imported ${parsedData.length} records!`);
      resetUpload();

    } catch (error: any) {
      console.error(error);
      toast.error('Import failed: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFinalizeBankImport = async () => {
    if (!currentUser) return;
    setIsProcessing(true);
    
    try {
        for (let idx = 0; idx < parsedData.length; idx++) {
            const row = parsedData[idx];
            const isApproved = approvedMatches[idx];
            const match = suggestedMatches[idx];
            
            const typeRaw = row[columnMapping['Type']]?.toLowerCase() || '';
            const isCredit = ['inward', 'credit', 'deposit'].includes(typeRaw);
            
            const item: any = {
                id: uuidv4(),
                userId: currentUser.uid,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                date: row[columnMapping['Date']],
                description: row[columnMapping['Description']],
                amount: parseFloat(row[columnMapping['Amount']]),
                type: isCredit ? 'credit' : 'debit',
                category: isApproved ? 'Invoice Payment' : 'Uncategorized',
            };

            await indexedDBService.addItem('bankTransactions', item);

            if (isApproved && match) {
                // Fulfill invoice
                await indexedDBService.updateItem('invoices', {
                    ...match,
                    status: 'paid',
                    amountPaid: match.totalAmount,
                    balanceDue: 0,
                    updatedAt: new Date().toISOString()
                });
            }
        }
        
        toast.success(`Successfully imported ${parsedData.length} bank transactions & processed reconciliations!`);
        resetUpload();
    } catch (e: any) {
        console.error(e);
        toast.error('Failed to commit bank records: ' + e.message);
    } finally {
        setIsProcessing(false);
    }
  };

  const hasValidationErrors = Object.keys(validationErrors).length > 0;
  const isImportDisabled = hasValidationErrors || parsedData.length === 0 || isProcessing;

  return (
    <Box p={{ xs: 2, md: 4 }}>
      <Box mb={4}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Bulk Data Import
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Migrate your existing business data seamlessly using our standardized CSV templates.
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Left Column: Instructions and Templates */}
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 3, mb: 4, height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="bold" mb={2} display="flex" alignItems="center" gap={1}>
                <InfoIcon color="primary" /> How it Works
              </Typography>
              <List>
                <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                    <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 'bold' }}>1</Box>
                  </ListItemIcon>
                  <ListItemText 
                    primary={<Typography fontWeight="bold">Download a Template</Typography>}
                    secondary="Select the specific data type below and download the strict CSV format."
                  />
                </ListItem>
                <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                    <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 'bold' }}>2</Box>
                  </ListItemIcon>
                  <ListItemText 
                    primary={<Typography fontWeight="bold">Populate Your Data</Typography>}
                    secondary="Fill the CSV using Excel or Google Sheets. Do not change the header names."
                  />
                </ListItem>
                <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                    <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 'bold' }}>3</Box>
                  </ListItemIcon>
                  <ListItemText 
                    primary={<Typography fontWeight="bold">Upload and Sync</Typography>}
                    secondary="Drag and drop your completed file to import the records into Akmo Books."
                  />
                </ListItem>
              </List>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" fontWeight="bold" mb={2}>
                Download Templates
              </Typography>
              
              <Grid container spacing={2}>
                {templates.map((tpl) => (
                  <Grid item xs={12} key={tpl.id}>
                    <Paper 
                      elevation={0} 
                      sx={{ 
                        p: 2, 
                        border: '1px solid', 
                        borderColor: 'divider',
                        borderRadius: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: tpl.color,
                          bgcolor: alpha(tpl.color, 0.04)
                        }
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Box sx={{ p: 1, borderRadius: 2, bgcolor: alpha(tpl.color, 0.1), flexShrink: 0 }}>
                          {tpl.icon}
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold" sx={{ lineHeight: 1.2, mb: 0.5 }}>{tpl.name}</Typography>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ wordBreak: 'break-all', lineHeight: 1.1 }}>{tpl.filename}</Typography>
                        </Box>
                      </Box>
                      <Button 
                        variant="outlined" 
                        size="small" 
                        fullWidth
                        startIcon={<DownloadIcon />}
                        onClick={() => generateAndDownloadCSV(tpl.filename, tpl.headers)}
                        sx={{ color: tpl.color, borderColor: alpha(tpl.color, 0.5), '&:hover': { borderColor: tpl.color, bgcolor: alpha(tpl.color, 0.1) } }}
                      >
                        Download Template
                      </Button>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column: Upload Area & Mapping */}
        <Grid item xs={12} md={8}>
          {!selectedFile ? (
            <Card sx={{ borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: 4, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" fontWeight="bold" mb={3}>
                  Upload Completed File
                </Typography>

                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel id="template-select-label">Target Template Type</InputLabel>
                  <Select
                    labelId="template-select-label"
                    value={selectedTemplate}
                    label="Target Template Type"
                    onChange={(e) => setSelectedTemplate(e.target.value as TemplateType)}
                  >
                    {templates.map(t => (
                      <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  component={motion.div}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px dashed',
                    borderColor: isDragging ? 'primary.main' : 'divider',
                    bgcolor: isDragging ? alpha(theme.palette.primary.main, 0.04) : (selectedTemplate ? 'background.default' : alpha(theme.palette.action.disabledBackground, 0.1)),
                    borderRadius: 4,
                    p: 4,
                    minHeight: 300,
                    transition: 'all 0.2s ease',
                    cursor: selectedTemplate ? 'pointer' : 'not-allowed',
                    opacity: selectedTemplate ? 1 : 0.6
                  }}
                  onClick={() => selectedTemplate && document.getElementById('file-upload-input')?.click()}
                >
                  <input 
                    type="file" 
                    id="file-upload-input" 
                    accept=".csv" 
                    style={{ display: 'none' }} 
                    onChange={handleFileInput}
                    disabled={!selectedTemplate}
                  />
                  
                  <Box textAlign="center">
                    <Box 
                      sx={{ 
                        width: 80, height: 80, 
                        borderRadius: '50%', 
                        bgcolor: alpha(theme.palette.primary.main, 0.1), 
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        mx: 'auto', mb: 2
                      }}
                    >
                      <CloudUploadIcon color="primary" sx={{ fontSize: 40 }} />
                    </Box>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      Drag & Drop CSV File
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={3}>
                      or click to browse from your computer
                    </Typography>
                    <Alert severity="info" sx={{ textAlign: 'left', display: 'inline-flex' }}>
                      Please select a target template type before uploading.
                    </Alert>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ) : (
            <Box display="flex" flexDirection="column" gap={3}>
              {!reconciliationMode ? (
                <>
                  {/* Mapping Interface */}
                  <Card sx={{ borderRadius: 3 }}>
                    <CardContent sx={{ p: 3 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Box>
                          <Typography variant="h6" fontWeight="bold">Column Mapping</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Match your uploaded file columns to our required schema.
                          </Typography>
                        </Box>
                        <Button variant="outlined" color="error" onClick={resetUpload} disabled={isProcessing}>
                          Cancel & Upload Different File
                        </Button>
                      </Box>
                      <Divider sx={{ mb: 3 }} />
                      
                      <Grid container spacing={2}>
                        {activeTemplateDef?.headers.map(reqHeader => {
                          const isRequired = activeTemplateDef.requiredHeaders.includes(reqHeader);
                          return (
                            <Grid item xs={12} sm={6} md={4} key={reqHeader}>
                              <FormControl fullWidth size="small" error={isRequired && !columnMapping[reqHeader]}>
                                <InputLabel>{reqHeader} {isRequired ? '*' : ''}</InputLabel>
                                <Select
                                  value={columnMapping[reqHeader] || ''}
                                  label={`${reqHeader} ${isRequired ? '*' : ''}`}
                                  onChange={(e) => handleMappingChange(reqHeader, e.target.value)}
                                  disabled={isProcessing}
                                >
                                  <MenuItem value=""><em>-- Ignore --</em></MenuItem>
                                  {parsedHeaders.map(h => (
                                    <MenuItem key={h} value={h}>{h}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </CardContent>
                  </Card>

                  {/* Data Preview Grid */}
                  <Card sx={{ borderRadius: 3 }}>
                    <CardContent sx={{ p: 0 }}>
                      <Box p={2} display="flex" justifyContent="space-between" alignItems="center" bgcolor="background.paper">
                        <Typography variant="h6" fontWeight="bold">Data Preview ({parsedData.length} Rows)</Typography>
                        <Box>
                          {hasValidationErrors ? (
                            <Chip icon={<ErrorIcon />} label={`${Object.keys(validationErrors).length} rows with errors`} color="error" />
                          ) : (
                            <Chip icon={<CheckCircleIcon />} label="All rows valid" color="success" />
                          )}
                        </Box>
                      </Box>
                      <Divider />
                      
                      <TableContainer sx={{ maxHeight: 400 }}>
                        <Table stickyHeader size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold', width: 100 }}>Status</TableCell>
                              {activeTemplateDef?.headers.map(h => (
                                <TableCell key={h} sx={{ fontWeight: 'bold' }}>{h}</TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {parsedData.map((row, idx) => {
                              const errors = validationErrors[idx];
                              const hasError = !!errors && errors.length > 0;
                              
                              return (
                                <TableRow 
                                  key={idx} 
                                  hover
                                  sx={{ 
                                    bgcolor: hasError ? alpha(theme.palette.error.main, 0.08) : 'inherit'
                                  }}
                                >
                                  <TableCell>
                                    {hasError ? (
                                      <Tooltip title={errors.join('\n')}>
                                        <Chip size="small" label="Error" color="error" icon={<ErrorIcon />} />
                                      </Tooltip>
                                    ) : (
                                      <Chip size="small" label="OK" color="success" />
                                    )}
                                  </TableCell>
                                  {activeTemplateDef?.headers.map(h => {
                                    const mappedKey = columnMapping[h];
                                    const cellValue = mappedKey ? row[mappedKey] : '';
                                    return (
                                      <TableCell key={h}>
                                        {cellValue || <Typography variant="caption" color="text.secondary">-</Typography>}
                                      </TableCell>
                                    );
                                  })}
                                </TableRow>
                              );
                            })}
                            {parsedData.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={(activeTemplateDef?.headers.length || 0) + 1} align="center" sx={{ py: 3 }}>
                                  No data found in the uploaded CSV.
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      <Box p={2} bgcolor="background.paper" display="flex" justifyContent="flex-end" gap={2}>
                        <Button 
                          variant="contained" 
                          color="primary"
                          disabled={isImportDisabled}
                          onClick={handleProcessData}
                          startIcon={isProcessing ? <CircularProgress size={20} color="inherit" /> : (activeTemplateDef?.id === 'bank' ? <MagicIcon /> : <CloudUploadIcon />)}
                        >
                          {activeTemplateDef?.id === 'bank' ? 'Scan & Reconcile' : 'Process & Import Data'}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </>
              ) : (
                /* Reconciliation Mode View */
                <Card sx={{ borderRadius: 3 }}>
                  <CardContent sx={{ p: 0 }}>
                    <Box p={3} bgcolor={alpha(theme.palette.info.main, 0.05)} borderBottom={`1px solid ${theme.palette.divider}`}>
                      <Typography variant="h5" fontWeight="bold" color="info.main" display="flex" alignItems="center" gap={1} gutterBottom>
                        <MagicIcon /> Smart Bank Reconciliation
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        We found unpaid invoices that match the amounts in your bank statement. Approve the matches below to automatically mark those invoices as Paid.
                      </Typography>
                    </Box>
                    
                    <TableContainer sx={{ maxHeight: 500 }}>
                      <Table stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Bank Date</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }} align="right">Amount</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', width: 350 }}>Reconciliation Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {parsedData.map((row, idx) => {
                            const date = row[columnMapping['Date']];
                            const desc = row[columnMapping['Description']];
                            const type = row[columnMapping['Type']];
                            const amount = parseFloat(row[columnMapping['Amount']]);
                            const match = suggestedMatches[idx];
                            const isApproved = approvedMatches[idx];
                            
                            return (
                              <TableRow key={idx} hover sx={{ bgcolor: isApproved ? alpha(theme.palette.success.main, 0.05) : 'inherit' }}>
                                <TableCell>{date}</TableCell>
                                <TableCell>{desc}</TableCell>
                                <TableCell>
                                  <Chip size="small" label={type} color={['inward', 'credit'].includes(type?.toLowerCase()) ? 'success' : 'error'} variant="outlined" />
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>₹{amount.toLocaleString('en-IN')}</TableCell>
                                <TableCell>
                                  {match ? (
                                    isApproved ? (
                                      <Chip 
                                        icon={<CheckCircleIcon />} 
                                        label={`Matched with Invoice #${match.invoiceNumber || match.id.slice(0,5)}`} 
                                        color="success" 
                                      />
                                    ) : (
                                      <Button 
                                        variant="contained" 
                                        size="small" 
                                        color="success" 
                                        onClick={() => setApprovedMatches(p => ({...p, [idx]: true}))}
                                      >
                                        Approve: Inv #{match.invoiceNumber || match.id.slice(0,5)}
                                      </Button>
                                    )
                                  ) : (
                                    <Typography variant="caption" color="text.secondary">
                                      No exact match found
                                    </Typography>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <Box p={3} bgcolor="background.paper" display="flex" justifyContent="space-between" alignItems="center">
                      <Button variant="outlined" color="error" onClick={resetUpload} disabled={isProcessing}>
                        Cancel Import
                      </Button>
                      <Button 
                        variant="contained" 
                        color="primary" 
                        size="large"
                        onClick={handleFinalizeBankImport}
                        disabled={isProcessing}
                        startIcon={isProcessing ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
                      >
                        Commit Bank Records
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default DataImport;
